// #2081 — Rate tables C: the per-client rate tables (#2079 data, #2080 API) on the web
// screens, behind the SERVER flag `client_rate_tables`.
//
// The screens never ask whether the flag is on — B's payloads say so, with no extra
// request: `getRates` rows carry `table_id` only while it is on (flag off = master's five
// global rates, `{id, name}`), and a skill carries `rate_table_amounts` only while it is on.
// Every helper here answers "off" for master's shapes, and every screen keeps master's
// code path, byte for byte, when it does.
//
// Vocabulary (B's `getRates` with the flag on, per live table, in position order):
//   id              what the old screens post as `rate_id`: legacy 1–5 for a back-filled
//                   table, the table uuid for one added later
//   name / alias    the table's alias
//   table_id        the table uuid — what THESE screens select and send
//   legacy_rate_id  1–5 or null

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function isUuid(value) {
    return typeof value === 'string' && UUID_RE.test(value);
}

/** True when a `getRates` result is B's flag-on feed (rows carry `table_id`). */
export function tablesOn(rates) {
    return Array.isArray(rates) && rates.length > 0 && !!rates[0] && isUuid(rates[0].table_id);
}

/** True when a skills list is B's flag-on payload (skills carry `rate_table_amounts`). */
export function skillsOn(skills) {
    return Array.isArray(skills) && skills.some((s) => !!s && typeof s === 'object' && Array.isArray(s.rate_table_amounts));
}

/** The live tables a `getRates` result describes, as `{table_id, alias, legacy_rate_id, position}`; [] when off. */
export function liveTables(rates) {
    if (!tablesOn(rates)) return [];
    return rates
        .filter((r) => r && isUuid(r.table_id))
        .map((r) => ({
            table_id: r.table_id,
            alias: r.alias !== undefined && r.alias !== null ? String(r.alias) : String(r.name === undefined || r.name === null ? '' : r.name),
            legacy_rate_id: r.legacy_rate_id === undefined || r.legacy_rate_id === null ? null : Number(r.legacy_rate_id),
            position: r.position === undefined || r.position === null ? 0 : Number(r.position),
        }));
}

/** The alias of a table id in a `getRates` result, or '' when it is not a live table. */
export function aliasOf(rates, tableId) {
    const t = liveTables(rates).find((x) => x.table_id === tableId);
    return t ? t.alias : '';
}

/** A skill's amount in one table ('' when it has none), from B's `rate_table_amounts`. */
export function amountFor(skill, tableId) {
    if (!skill || !Array.isArray(skill.rate_table_amounts) || !tableId) return '';
    const row = skill.rate_table_amounts.find((a) => a && a.client_rate_table_id === tableId);
    if (!row || row.amount === null || row.amount === undefined) return '';
    return row.amount;
}

/** `{table_id: amount|''}` for every table given, for an edit form. */
export function amountsFor(skill, tables) {
    const out = {};
    (tables || []).forEach((t) => { out[t.table_id] = amountFor(skill, t.table_id); });
    return out;
}

/**
 * The `amounts` object a PUT skill/{id} sends: every table key, each value trimmed. B treats
 * '' as "no change for that table" and never deletes, so a blank input is harmless.
 */
export function amountsPayload(edits) {
    const out = {};
    Object.keys(edits || {}).forEach((tableId) => {
        if (!isUuid(tableId)) return;
        const v = edits[tableId];
        out[tableId] = v === null || v === undefined ? '' : String(v).trim();
    });
    return out;
}

/**
 * What a job form sends for its rate choice. Flag off: master's `rate_id` (the legacy id
 * the select holds). Flag on: the chosen table in `client_rate_table_id` — B resolves the
 * legacy `rate_id` from the table itself, and an explicit (even empty) table key is what
 * lets a job's table be cleared from a screen that can see it.
 */
export function jobRateFields(on, choice) {
    if (!on) return { rate_id: choice };
    return { rate_id: '', client_rate_table_id: choice === null || choice === undefined ? '' : choice };
}

/** The select value to pre-fill a job form with. Flag on: the job's table id; off: master's `rate_id`. */
export function jobRateChoice(on, job) {
    if (!job) return '';
    if (!on) return job.rate_id ? job.rate_id : '';
    return job.client_rate_table_id ? job.client_rate_table_id : '';
}

/**
 * Fill-once (Ed, 2026-10-11; #2083 keeps it): on employer SELECTION the employer's default
 * rate table (#2083, set on the Employer screen) fills the job's rate table — and only when
 * the current choice is empty or was itself a default fill. A table the user picked by hand,
 * or one already stored on the job, is never overwritten. Pure: returns the next state for
 * the form to apply.
 *
 * @param {object} state   {choice, pickedByHand, filledFromDefault}
 * @param {object|null} def  the LIVE table the API answers for the employer, or null
 */
export function applyEmployerDefault(state, def) {
    const s = state || {};
    const current = s.choice === null || s.choice === undefined ? '' : s.choice;
    if (s.pickedByHand) return { choice: current, pickedByHand: true, filledFromDefault: !!s.filledFromDefault, changed: false };
    if (current !== '' && !s.filledFromDefault) return { choice: current, pickedByHand: false, filledFromDefault: false, changed: false };
    if (!def || !isUuid(def.id)) return { choice: current, pickedByHand: false, filledFromDefault: !!s.filledFromDefault, changed: false };
    return { choice: def.id, pickedByHand: false, filledFromDefault: true, changed: def.id !== current };
}

/** Headers every rate-table call carries (the same three the screens already send). */
export function rateTableHeaders(ctx) {
    return {
        'Authorization': ctx.userToken,
        'cs3-context-role': ctx.userRole,
        'cs3-context-clientId': ctx.userClientID,
    };
}

export function rateTablesPath(clientId, suffix = '') {
    return 'client/' + clientId + '/rate-tables' + (suffix ? '/' + suffix : '');
}

/** #2083 — the employer's default rate table for the client: GET / PUT {client_rate_table_id} / DELETE. */
export function employerDefaultPath(clientId, employerId) {
    return rateTablesPath(clientId, 'employer/' + encodeURIComponent(employerId));
}
